/**
 * <SplitHero> — the signature PolicyOra two-column auth/onboarding layout.
 *
 *   ┌─────────────────────┬──────────────────────┐
 *   │  dark brand panel   │  light form / task   │
 *   │  (left slot)        │  (right slot)        │
 *   └─────────────────────┴──────────────────────┘
 *
 * Both slots accept any children; consumers compose their own content
 * using <Eyebrow>, <PageHeading>, <SwatchCard>, <StatsStrip>, <TrustStrip>.
 *
 * Below 880 px the brand panel hides and the form takes full width.
 *
 * This is intentionally a layout primitive only — it does NOT prescribe
 * what goes in each pane.
 */
import React from 'react';
import styles from './SplitHero.module.css';

interface SplitHeroProps {
  left: React.ReactNode;
  right: React.ReactNode;
  /** Header content rendered above the split (e.g. logo + theme toggle row). */
  header?: React.ReactNode;
  /** Width ratio for the left pane. Defaults to "44%". */
  leftWidth?: string;
  className?: string;
}

export const SplitHero: React.FC<SplitHeroProps> = ({
  left,
  right,
  header,
  leftWidth = '44%',
  className = '',
}) => {
  return (
    <div className={`${styles.root} ${className}`}>
      {header && <div className={styles.header}>{header}</div>}
      <div className={styles.split}>
        <aside
          className={styles.brand}
          style={{ flexBasis: leftWidth }}
        >
          <div className={styles.brandContent}>{left}</div>
        </aside>
        <main className={styles.form}>
          <div className={styles.formContent}>{right}</div>
        </main>
      </div>
    </div>
  );
};
